import type {
  AddSiteMeasurementTargetBody,
  AddSiteMeasurementTargetResponse,
  SiteMeasurementTargetsResponse,
  SiteWorkTypesResponse,
} from '@safetyhub/shared';
import { api, type ApiResponse } from './api';

/**
 * 법정 서류 — **현장 사실 명부**의 API 클라이언트(②-c 2부 E6 특별교육 대상 작업 · E7 ⓒ 작업환경측정 대상 공정 × 유해인자).
 * 계약 정본은 shared `features/legal-obligation-evidence.ts`의 명부 절. 모든 호출이 `?companyId=`를 싣는다(첫 인자 필수).
 * ⚠️ 화면 spec이 이 모듈을 `vi.mock`할 수 있으니 순수 술어·라벨을 여기 두지 않는다(형제 `legalObligationEvidence.ts`와 같은 규약).
 */

export const legalSiteFactsQueryKeys = {
  workTypes: (companyId: string, siteId: string) => ['legal-documents', companyId, 'sites', siteId, 'work-types'] as const,
  measurementTargets: (companyId: string, siteId: string) =>
    ['legal-documents', companyId, 'sites', siteId, 'measurement-targets'] as const,
};

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) throw body;
  return body.data;
}

function sitePath(siteId: string): string {
  return `/legal-documents/sites/${encodeURIComponent(siteId)}`;
}

export async function getSiteWorkTypes(companyId: string, siteId: string): Promise<SiteWorkTypesResponse> {
  const { data } = await api.get<ApiResponse<SiteWorkTypesResponse>>(`${sitePath(siteId)}/work-types`, { params: { companyId } });
  return unwrap(data);
}

export async function addSiteWorkType(companyId: string, siteId: string, code: number): Promise<{ id: string; created: boolean }> {
  const { data } = await api.post<ApiResponse<{ id: string; created: boolean }>>(
    `${sitePath(siteId)}/work-types`,
    { code },
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function endSiteWorkType(companyId: string, siteId: string, entryId: string): Promise<{ id: string }> {
  const { data } = await api.delete<ApiResponse<{ id: string }>>(`${sitePath(siteId)}/work-types/${encodeURIComponent(entryId)}`, {
    params: { companyId },
  });
  return unwrap(data);
}

export async function setSiteWorkTypesReviewed(
  companyId: string,
  siteId: string,
  reviewed: boolean
): Promise<{ reviewedAt: string | null }> {
  const { data } = await api.put<ApiResponse<{ reviewedAt: string | null }>>(
    `${sitePath(siteId)}/work-types/review`,
    { reviewed },
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function getSiteMeasurementTargets(companyId: string, siteId: string): Promise<SiteMeasurementTargetsResponse> {
  const { data } = await api.get<ApiResponse<SiteMeasurementTargetsResponse>>(`${sitePath(siteId)}/measurement-targets`, {
    params: { companyId },
  });
  return unwrap(data);
}

export async function addSiteMeasurementTarget(
  companyId: string,
  siteId: string,
  body: AddSiteMeasurementTargetBody
): Promise<AddSiteMeasurementTargetResponse> {
  const { data } = await api.post<ApiResponse<AddSiteMeasurementTargetResponse>>(`${sitePath(siteId)}/measurement-targets`, body, {
    params: { companyId },
  });
  return unwrap(data);
}

export async function endSiteMeasurementTarget(companyId: string, siteId: string, targetId: string): Promise<{ id: string }> {
  const { data } = await api.delete<ApiResponse<{ id: string }>>(
    `${sitePath(siteId)}/measurement-targets/${encodeURIComponent(targetId)}`,
    { params: { companyId } }
  );
  return unwrap(data);
}
